import type { ImportCommitResponse } from "../import/externalRegisterImportTypes";
import {
  buildExternalRegisterImportStorageKey,
  getBrowserStorage,
  safeJsonParse,
  safeJsonSetItem,
  safeStorageGetItem,
  safeStorageRemoveItem,
  safeStorageRemoveItems,
} from "../shared/externalRegisterStorage";

export const LEGACY_EXTERNAL_REGISTER_IMPORT_HISTORY_KEY = "external-register-import-summary-history-v1";
export const EXTERNAL_REGISTER_IMPORT_HISTORY_KEY = buildExternalRegisterImportStorageKey("external-register-import-summary-history-v1");

export type ExternalRegisterImportHistorySummary = {
  id: string;
  completedAt: string;
  clientImportId: string;
  sourceFingerprint: string;
  status: ImportCommitResponse["status"];
  totalRecords: number;
  successfullyImported: number;
  errors: number;
  skippedOrExcluded: number;
  retryableRows: number;
  matchedOnUmrn: number;
  matchedOnDemographics: number;
  newPatientsCreated: number;
  updatedExistingEntries: number;
  confidenceCounts: {
    HIGH: number;
    MEDIUM: number;
    LOW: number;
    ERROR: number;
  };
};

const MAX_HISTORY_ITEMS = 10;

export function buildImportHistorySummary(response: ImportCommitResponse): ExternalRegisterImportHistorySummary {
  return {
    id: `${response.clientImportId}-${Date.now()}`,
    completedAt: new Date().toISOString(),
    clientImportId: response.clientImportId,
    sourceFingerprint: response.sourceFingerprint,
    status: response.status,
    totalRecords: response.totalRecords,
    successfullyImported: response.successfullyImported,
    errors: response.errors,
    skippedOrExcluded: response.skippedOrExcluded,
    retryableRows: response.retryableRows,
    matchedOnUmrn: response.matchedOnUmrn,
    matchedOnDemographics: response.matchedOnDemographics,
    newPatientsCreated: response.newPatientsCreated,
    updatedExistingEntries: response.updatedExistingEntries,
    confidenceCounts: response.rows.reduce(
      (counts, row) => ({
        ...counts,
        [row.matchConfidence]: counts[row.matchConfidence] + 1,
      }),
      { HIGH: 0, MEDIUM: 0, LOW: 0, ERROR: 0 },
    ),
  };
}

export const importHistoryAdapter = {
  load(): ExternalRegisterImportHistorySummary[] {
    const storage = getBrowserStorage("local");
    const stored = safeStorageGetItem(storage, EXTERNAL_REGISTER_IMPORT_HISTORY_KEY)
      ?? safeStorageGetItem(storage, LEGACY_EXTERNAL_REGISTER_IMPORT_HISTORY_KEY);
    if (!stored) return [];

    const parsed = safeJsonParse<ExternalRegisterImportHistorySummary[]>(stored);
    if (!parsed) {
      safeStorageRemoveItems(storage, [
        EXTERNAL_REGISTER_IMPORT_HISTORY_KEY,
        LEGACY_EXTERNAL_REGISTER_IMPORT_HISTORY_KEY,
      ]);
      return [];
    }
    if (safeStorageGetItem(storage, EXTERNAL_REGISTER_IMPORT_HISTORY_KEY) === null) {
      safeJsonSetItem(storage, EXTERNAL_REGISTER_IMPORT_HISTORY_KEY, parsed);
      safeStorageRemoveItem(storage, LEGACY_EXTERNAL_REGISTER_IMPORT_HISTORY_KEY);
    }
    return Array.isArray(parsed) ? parsed : [];
  },

  add(summary: ExternalRegisterImportHistorySummary): ExternalRegisterImportHistorySummary[] {
    const storage = getBrowserStorage("local");
    const next = [summary, ...this.load()].slice(0, MAX_HISTORY_ITEMS);
    safeJsonSetItem(storage, EXTERNAL_REGISTER_IMPORT_HISTORY_KEY, next);
    safeStorageRemoveItem(storage, LEGACY_EXTERNAL_REGISTER_IMPORT_HISTORY_KEY);
    return next;
  },

  dismiss(id: string): ExternalRegisterImportHistorySummary[] {
    const storage = getBrowserStorage("local");
    const next = this.load().filter((item) => item.id !== id);
    safeJsonSetItem(storage, EXTERNAL_REGISTER_IMPORT_HISTORY_KEY, next);
    safeStorageRemoveItem(storage, LEGACY_EXTERNAL_REGISTER_IMPORT_HISTORY_KEY);
    return next;
  },
};
